// Single source of truth for "how long ago was this?" and date rendering.
// Pre-cleanup: 7+ files each ran their own `${diffMins}m ago` chain, all
// slightly different (3 vs 5 tiers, with/without "Just now", varying
// fallbacks for >7d). Route every call site through here so future
// changes (locale, "ago" vs naked, week tier, weekday) happen in one
// place. Same pattern as `formatName`, `formatPrice`, `formatHandle`.

export type DateInput = string | Date | number | null | undefined;

const toDate = (input: DateInput): Date | null => {
  if (input === null || input === undefined || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Compact relative time. Use for any chronological surface — feed
 * timestamps, activity rows, notifications, conversation previews.
 *
 *   < 1m   → "Just now"
 *   < 1h   → "5m ago"
 *   < 1d   → "3h ago"
 *   < 1w   → "2d ago"
 *   < 1y   → "Apr 5"
 *   else   → "Apr 5, 2023"
 */
export const formatRelativeTime = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatCalendarDate(d);
};

/** "Apr 5" if this year, else "Apr 5, 2023". */
export const formatCalendarDate = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  const isThisYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(isThisYear ? {} : { year: 'numeric' }),
  });
};

/** "Apr 5" — short calendar date, no year. */
export const formatShortDate = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/** "Apr 5, 2023" — full calendar date with year. */
export const formatFullDate = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/** "3:45 PM" — time only, 12-hour. */
export const formatClockTime = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * "Apr 5 · 3:45 PM" (this year) or "Apr 5, 2023 · 3:45 PM" (older).
 * Use when you want both date and clock time on one line, e.g. message
 * detail timestamps.
 */
export const formatDateTime = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${formatCalendarDate(d)} · ${formatClockTime(d)}`;
};
